import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { APIResultListStockExchangeResponse, CompanyWithSlotPeriodIdsResponse, EventInfoWithConsentResponse, InvestorCompanyResponse, InvestorMeetingCreateRequest, RegisterCompanyPublicControllerService, StockExchangeControllerService, StockExchangeResponse } from '@ent-regis/entregis-ts-angular';
import { AttendancePreferenceEnum, FilterParams } from 'src/app/register-investor/models/register-investor.model';

const BANGKOK_UTC_OFFSET_MS = 7 * 60 * 60 * 1000;

@Component({
  selector: 'app-investor-company',
  templateUrl: './investor-company.component.html',
  styleUrls: ['./investor-company.component.scss']
})
export class InvestorCompanyComponent implements OnInit {

  @Input() form: FormGroup;
  @Input() eventInfoWithConsent!: EventInfoWithConsentResponse;
  @Input() isClosedRegis: boolean = false;
  @Input() defaultMeetingType!: InvestorMeetingCreateRequest.MeetingTypeEnum;
  @Input() defaultMeetingPriority!: InvestorMeetingCreateRequest.MeetingPriorityEnum;
  @Input() attendancePreference!: AttendancePreferenceEnum;
  @Output() onSuccessEvent = new EventEmitter<any>();

  approvedCompanies: CompanyWithSlotPeriodIdsResponse[] = [];
  filteredCompanies: CompanyWithSlotPeriodIdsResponse[] = [];
  sectorNames: string[] = [];
  stockExchanges: StockExchangeResponse[] = [];
  now = Date.now()

  get selectedCount(): number {
    const companies = this.form.get('companies') as FormArray;
    return companies.length;
  }

  get isThailandFocus(): boolean {
    return !!this.eventInfoWithConsent && this.eventInfoWithConsent.eventType === EventInfoWithConsentResponse.EventTypeEnum.THAILANDFOCUS;
  }

  constructor(
    private registerCompanyPublicService: RegisterCompanyPublicControllerService,
    private stockExchangeService: StockExchangeControllerService,
  ) { }

  ngOnInit() {
    this.stockExchangeService.getAllStockExchangesUsingGET()
      .subscribe((response: APIResultListStockExchangeResponse) => {
        this.stockExchanges = response.data || [];
      });
  }

  ngOnChanges() {
    this.updateFormState();
    if (this.eventInfoWithConsent && this.eventInfoWithConsent.eventAlias) {
      this.getApprovedCompany(this.eventInfoWithConsent.eventAlias);
    }
  }

  onSubmit() {
    if (this.isClosedRegis) {
      return;
    }
    this.onSuccessEvent.emit(this.form.value.companies);
  }

  private updateFormState(): void {
    if (!this.form) {
      return;
    }

    if (this.isClosedRegis) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });
  }

  private getApprovedCompany(eventAlias: string) {
    this.registerCompanyPublicService.getApprovedRegisterCompanyUsingGET(eventAlias)
      .subscribe(res => {
        if (!res.data) return;
        this.approvedCompanies = res.data;
        this.filteredCompanies = res.data;
        this.sectorNames = [
          ...new Set(
            res.data
              .map(company => company.sectorName)
              .filter((name): name is string => Boolean(name))
          )
        ].sort();
    });
  }

  applyFilter(params: FilterParams) {
    let result = [...this.approvedCompanies];

    if (!this.isValidExchange(params.exchange)) {
      this.filteredCompanies = [];
      return;
    }

    result = this.filterByExchange(result, params.exchange);
    result = this.filterByViewJumpplus(result, params.viewJumpplus);
    result = this.filterBySearch(result, params.search);
    result = this.filterByDateRange(result, params.fromDate, params.toDate);
    result = this.filterBySector(result, params.sector);

    this.sortResult(result, params.order);

    this.filteredCompanies = result;
  }

  private isValidExchange(exchange: any): boolean {
    const selected = Array.isArray(exchange) ? exchange : [];
    return selected.length > 0;
  }

  private filterByExchange(data: CompanyWithSlotPeriodIdsResponse[], exchange?: string[]): CompanyWithSlotPeriodIdsResponse[] {
    if (!exchange) return data;
    
    const hasSET = exchange.includes('SET');
    const hasOTHER = exchange.includes('OTHER');

    if (hasSET && hasOTHER) return data;

    return data.filter(c =>
      hasSET ? c.stockExchangeId === 5 : c.stockExchangeId !== 5
    );
  }

  private filterByViewJumpplus(data: CompanyWithSlotPeriodIdsResponse[], viewJumpplus?: boolean): CompanyWithSlotPeriodIdsResponse[] {
    if (viewJumpplus === false) return data;

    return data.filter(c => c.listedJumpplusId !== null);
  }

  private filterBySearch(data: CompanyWithSlotPeriodIdsResponse[], search?: string | null): CompanyWithSlotPeriodIdsResponse[] {
    if (!search) return data;
    const keyword = search.trim().toLowerCase();

    return data.filter(c =>
      (c.companyName && c.companyName.toLowerCase().includes(keyword)) ||
      (c.companySymbol && c.companySymbol.toLowerCase().includes(keyword))
    );
  }

  private filterByDateRange(data: CompanyWithSlotPeriodIdsResponse[], fromDate?: Date|string|null, toDate?: Date|string|null): CompanyWithSlotPeriodIdsResponse[] {
    if (!fromDate && !toDate) return data;

    const start = this.getStartOfDay(fromDate);
    const end = this.getEndOfDay(toDate);

    return data.filter(c =>
      c.slotPeriods && c.slotPeriods.some(slot =>
        this.isValidSlot(slot.slotStartTime, start, end)
      )
    );
  }

  private isValidSlot(slotStartTime?: string | Date, start?: number | null, end?: number | null): boolean {
    if (!slotStartTime) return false;
    const time = new Date(slotStartTime).getTime();
    return (!start || time >= start) && (!end || time <= end);
  }

  private getStartOfDay(date?: Date | string | null): number | null {
    if (!date) return null;
    const d = new Date(date);
    if (!this.isThailandFocus) {
      return new Date(d).setHours(0, 0, 0, 0);
    }
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0) - BANGKOK_UTC_OFFSET_MS;
  }

  private getEndOfDay(date?: Date | string | null): number | null {
    if (!date) return null;
    const d = new Date(date);
    if (!this.isThailandFocus) {
      return new Date(d).setHours(23, 59, 59, 999);
    }
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999) - BANGKOK_UTC_OFFSET_MS;
  }

  private filterBySector(data: CompanyWithSlotPeriodIdsResponse[], sector?: string | null): CompanyWithSlotPeriodIdsResponse[] {
    if (!sector) return data;
    const targetSector = sector.trim().toLowerCase();

    return data.filter(c =>
      c.sectorName && c.sectorName.trim().toLowerCase() === targetSector
    );
  }

  private sortResult(data: CompanyWithSlotPeriodIdsResponse[], order?: string): void {
    if (!order) return;

    data.sort((a, b) => {
      const aVal = a.companySymbol && a.companySymbol.toLowerCase() || '';
      const bVal = b.companySymbol && b.companySymbol.toLowerCase() || '';

      return order === 'ASC' 
        ? aVal.localeCompare(bVal) 
        : bVal.localeCompare(aVal);
    });
  }

}
